import { useEffect, useState } from 'react';
import { Button, Modal, Space, Table, Tabs, Tag, Typography, message } from 'antd';
import { Eye, RefreshCw } from 'lucide-react';
import { avmApi } from '../../services/api';

const { Text, Title } = Typography;

interface EmailListRow {
  id: string;
  leadId?: string | null;
  clientEmail: string;
  clientName?: string | null;
  clientSubject: string;
  internalTo: string;
  internalSubject: string;
  estimatedValue?: number | null;
  estimatedLow?: number | null;
  estimatedHigh?: number | null;
  zone?: string | null;
  neighborhood?: string | null;
  propCategory?: string | null;
  ownerType?: string | null;
  createdAt: string;
}

interface EmailDetail extends EmailListRow {
  clientHtml: string;
  internalHtml: string;
  traceJson?: unknown;
}

const fmt = (n?: number | null) =>
  n == null
    ? '—'
    : `${new Intl.NumberFormat('es-ES', { maximumFractionDigits: 0 }).format(n)} €`;

export default function AvmValoradorEmailsTab() {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<EmailListRow[]>([]);
  const [detail, setDetail] = useState<EmailDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);

  const fetchList = async () => {
    setLoading(true);
    try {
      const { data } = await avmApi.listValoradorEmails(100);
      setRows(Array.isArray(data) ? data : []);
    } catch {
      message.error('No se pudieron cargar los emails del valorador');
    } finally {
      setLoading(false);
    }
  };

  const sendTest = async () => {
    setSendingTest(true);
    try {
      const { data } = await avmApi.sendTestValoradorEmail({
        name: 'Emanuel',
        email: 'emanuelvm04@gmail.com',
      });
      message.success(`Prueba enviada a ${data.clientEmail} (interno: ${data.internalTo})`);
      await fetchList();
    } catch {
      message.error('No se pudo enviar el email de prueba');
    } finally {
      setSendingTest(false);
    }
  };

  useEffect(() => {
    fetchList();
  }, []);

  const openDetail = async (id: string) => {
    setDetailLoading(true);
    try {
      const { data } = await avmApi.getValoradorEmail(id);
      setDetail(data);
    } catch {
      message.error('No se pudo cargar el detalle del email');
    } finally {
      setDetailLoading(false);
    }
  };

  const columns = [
    {
      title: 'Fecha',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (v: string) =>
        new Date(v).toLocaleString('es-ES', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        }),
    },
    {
      title: 'Cliente',
      key: 'client',
      render: (_: unknown, r: EmailListRow) => (
        <div>
          <Text strong style={{ display: 'block' }}>{r.clientName || '—'}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>{r.clientEmail}</Text>
        </div>
      ),
    },
    {
      title: 'Zona',
      key: 'zone',
      width: 180,
      render: (_: unknown, r: EmailListRow) =>
        [r.neighborhood, r.zone].filter(Boolean).join(' · ') || '—',
    },
    {
      title: 'Estimación',
      dataIndex: 'estimatedValue',
      key: 'estimatedValue',
      width: 120,
      render: (v: number | null) => <Text strong>{fmt(v)}</Text>,
    },
    {
      title: 'Tipo',
      key: 'type',
      width: 140,
      render: (_: unknown, r: EmailListRow) => (
        <Space size={4} wrap>
          {r.propCategory && <Tag>{r.propCategory}</Tag>}
          {r.ownerType && <Tag color="blue">{r.ownerType}</Tag>}
        </Space>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 100,
      render: (_: unknown, r: EmailListRow) => (
        <Button
          size="small"
          icon={<Eye size={14} />}
          loading={detailLoading}
          onClick={() => openDetail(r.id)}
        >
          Ver
        </Button>
      ),
    },
  ];

  return (
    <>
      <Space style={{ marginBottom: 16 }} align="center">
        <Title level={5} style={{ margin: 0 }}>Emails valorador (temporal)</Title>
        <Tag color="orange">TEMP</Tag>
        <Button size="small" icon={<RefreshCw size={14} />} onClick={fetchList} loading={loading}>
          Actualizar
        </Button>
        <Button size="small" type="primary" loading={sendingTest} onClick={sendTest}>
          Enviar prueba
        </Button>
      </Space>
      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
        Vista de lo enviado al cliente y al correo interno en cada valoración.
      </Text>

      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={rows}
        size="small"
        pagination={{ pageSize: 20 }}
        scroll={{ x: 900 }}
      />

      <Modal
        title={detail ? `${detail.clientName || detail.clientEmail} · ${fmt(detail.estimatedValue)}` : 'Email'}
        open={!!detail}
        onCancel={() => setDetail(null)}
        footer={null}
        width="min(960px, 96vw)"
        styles={{ body: { paddingTop: 8 } }}
      >
        {detail && (
          <Tabs
            items={[
              {
                key: 'client',
                label: 'Email al cliente',
                children: (
                  <>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 8, fontSize: 12 }}>
                      Para: {detail.clientEmail} · Asunto: {detail.clientSubject}
                    </Text>
                    <iframe
                      title="client-email"
                      srcDoc={detail.clientHtml}
                      style={{
                        width: '100%',
                        height: '65vh',
                        border: '1px solid #e5e7eb',
                        borderRadius: 4,
                        background: '#fff',
                      }}
                      sandbox=""
                    />
                  </>
                ),
              },
              {
                key: 'internal',
                label: 'Email interno',
                children: (
                  <>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 8, fontSize: 12 }}>
                      Para: {detail.internalTo} · Asunto: {detail.internalSubject}
                    </Text>
                    <iframe
                      title="internal-email"
                      srcDoc={detail.internalHtml}
                      style={{
                        width: '100%',
                        height: '65vh',
                        border: '1px solid #e5e7eb',
                        borderRadius: 4,
                        background: '#fff',
                      }}
                      sandbox=""
                    />
                  </>
                ),
              },
            ]}
          />
        )}
      </Modal>
    </>
  );
}
